import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, ReplaySubject, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { Navigation } from 'app/core/navigation/navigation.types';
import { GlobalVariablesService } from '@app/core/services/global-variables.service';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { cloneDeep } from 'lodash-es';
import { TranslateService } from '@ngx-translate/core';
import { BadisOauthLoginService } from '@app/core/services/auth/badis-oauth-login.service';
import { AppPermissionService } from '@app/core/services/app-permission.service';
import { ThirdPartyAppsFeatureService } from '@app/core/services/third-party-apps-feature.service';
import { SettingsService } from '@app/core/services/settings/settings.service';

@Injectable({
    providedIn: 'root',
})
export class NavigationService {
    private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);
    private _rawMenu: FuseNavigationItem[] | null = null;

    constructor(
        private _httpClient: HttpClient,
        private _globals: GlobalVariablesService,
        private _translate: TranslateService,
        private _authService: BadisOauthLoginService,
        private _permissionService: AppPermissionService,
        private _thirdPartyAppsFeature: ThirdPartyAppsFeatureService,
        private _settingsService: SettingsService
    ) {
        this._translate.onLangChange.subscribe(() => {
            if (this._rawMenu) {
                this.publishMenu(this._rawMenu);
            }
        });
        this._translate.onTranslationChange.subscribe(() => {
            if (this._rawMenu) {
                this.publishMenu(this._rawMenu);
            }
        });
        this._settingsService.settingChanged.subscribe(setting => {
            if (setting?.primaryKey === 'ThirdPartyApps.Enabled' && this._rawMenu) {
                this.publishMenu(this._rawMenu);
            }
        });
        this._settingsService.isReady.subscribe(isReady => {
            if (isReady === true && this._rawMenu) {
                this.publishMenu(this._rawMenu);
            }
        });
    }

    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    get(): Observable<Navigation> {
        return this._httpClient
            .get<FuseNavigationItem[]>(
                `${this._globals.apiV2Address}/client-services/get-system-menu`
            )
            .pipe(
                switchMap((menu) => this.prepareMenu(menu))
            );
    }

    setMenu(menu: FuseNavigationItem[]): void {
        this._rawMenu = cloneDeep(menu || []);
        this._globals.navizardSystemMenuSource = cloneDeep(this._rawMenu);
        this.publishMenu(this._rawMenu);
    }

    private buildNavigation(menu: FuseNavigationItem[]): Navigation {
        const defaultNav = this.translateNavigation(cloneDeep(menu));
        return {
            default: defaultNav,
            compact: cloneDeep(defaultNav),
            horizontal: cloneDeep(defaultNav),
        };
    }

    private prepareMenu(menu: FuseNavigationItem[]): Observable<Navigation> {
        const build = () => {
            this._rawMenu = this.mergeRuntimeNavigationItems(menu || [], this._rawMenu);
            return this.publishMenu(this._rawMenu);
        };

        if (!this._authService.isUserLoggedIn()) {
            return of(build());
        }

        return this._permissionService.loadCurrent().pipe(
            map(() => build())
        );
    }

    private mergeRuntimeNavigationItems(baseMenu: FuseNavigationItem[], currentMenu: FuseNavigationItem[] | null): FuseNavigationItem[] {
        const nextMenu = cloneDeep(baseMenu || []);
        if (!currentMenu?.length) {
            return nextMenu;
        }

        this.mergeRuntimeChildById(nextMenu, currentMenu, 'reports', 'custom-reports');
        return nextMenu;
    }

    private mergeRuntimeChildById(
        targetRoot: FuseNavigationItem[],
        sourceRoot: FuseNavigationItem[],
        parentId: string,
        childId: string
    ): void {
        const sourceParent = this.findNavigationItem(sourceRoot, parentId);
        const sourceChild = sourceParent?.children?.find((child) => child.id === childId);
        if (!sourceChild) {
            return;
        }

        const targetParent = this.findNavigationItem(targetRoot, parentId);
        if (!targetParent) {
            return;
        }

        targetParent.children = (targetParent.children || []).filter((child) => child.id !== childId);
        targetParent.children.push(cloneDeep(sourceChild));
    }

    private findNavigationItem(items: FuseNavigationItem[], id: string): FuseNavigationItem | null {
        for (const item of items || []) {
            if (item.id === id) {
                return item;
            }

            const child = this.findNavigationItem(item.children || [], id);
            if (child) {
                return child;
            }
        }

        return null;
    }

    private publishMenu(menu: FuseNavigationItem[]): Navigation {
        this._globals.navizardSystemMenuSource = cloneDeep(menu || []);
        const filteredMenu = this.applyVisibilityRules(cloneDeep(menu || []), this._permissionService.isSuperAdminSync());
        this._globals.navizardSystemMenu = filteredMenu;
        const navigation = this.buildNavigation(filteredMenu);
        this._navigation.next(navigation);
        return navigation;
    }

    private applyVisibilityRules(items: FuseNavigationItem[], isSuperAdmin = false): FuseNavigationItem[] {
        const filteredItems = this._thirdPartyAppsFeature.isEnabled()
            ? items
            : this.removeThirdPartyAppsMenuItems(items);

        return filteredItems.map((item: any) => {
            const nextItem = { ...item };
            if (nextItem.children?.length) {
                nextItem.children = this.applyVisibilityRules(nextItem.children, isSuperAdmin);
            }

            if (this.isDashboardHomeItem(nextItem)) {
                nextItem.hidden = false;
            } else if (isSuperAdmin) {
                nextItem.hidden = false;
            } else {
                const permissionTarget = nextItem.screenCode || nextItem.id;
                const canView = this._permissionService.canViewSync(permissionTarget);
                const hasRoles = Array.isArray(nextItem.roles) && nextItem.roles.length > 0;
                const isInRoles = !hasRoles || this._authService.isUserInRoles(nextItem.roles || []);
                const hiddenBySelf = nextItem.hide || (canView === false) || (canView === null && hasRoles && !isInRoles);
                const hasVisibleChildren = nextItem.children?.some((child) => this.isNavigationItemVisible(child)) === true;

                nextItem.hidden = hiddenBySelf && !hasVisibleChildren;
            }

            return nextItem;
        });
    }

    private isNavigationItemVisible(item: FuseNavigationItem): boolean {
        if (!item) {
            return false;
        }

        if (typeof item.hidden === 'function') {
            return !item.hidden(item);
        }

        return item.hidden !== true;
    }

    private isDashboardHomeItem(item: FuseNavigationItem): boolean {
        return String(item?.id ?? '').trim().toLocaleLowerCase() === 'dashboards-home'
            || String((item as any)?.screenCode ?? '').trim().toLocaleLowerCase() === 'dashboards-home'
            || String(item?.link ?? '').trim().toLocaleLowerCase() === '/dashboards/home';
    }

    private removeThirdPartyAppsMenuItems(items: FuseNavigationItem[]): FuseNavigationItem[] {
        return (items || [])
            .map((item: any) => {
                if (this.isThirdPartyAppsMenuItem(item)) {
                    return null;
                }

                const children = this.removeThirdPartyAppsMenuItems(item.children || []);
                const nextItem = { ...item };

                if (children.length > 0) {
                    nextItem.children = children;
                } else {
                    delete nextItem.children;
                }

                return nextItem;
            })
            .filter(Boolean) as FuseNavigationItem[];
    }

    private isThirdPartyAppsMenuItem(menuItem: any): boolean {
        const id = String(menuItem?.id ?? '').trim().toLocaleLowerCase();
        const link = String(menuItem?.link ?? '').trim().toLocaleLowerCase();
        const screenCode = String(menuItem?.screenCode ?? '').trim().toLocaleLowerCase();
        const title = String(menuItem?.title ?? '').trim().toLocaleLowerCase();
        const subtitle = String(menuItem?.subtitle ?? '').trim().toLocaleLowerCase();

        return id === 'third-party-apps'
            || id === 'third-party-app-store'
            || id === 'maintenances-app-store'
            || id === 'apps'
            || id === 'app-store'
            || id.startsWith('third-party-app-')
            || screenCode.startsWith('third-party-app')
            || screenCode === 'third-party-app-store'
            || title === 'apps'
            || title === 'app store'
            || title === 'uygulamalar'
            || title === 'uygulama mağazası'
            || title.startsWith('systemadmin.menu.apps')
            || subtitle === 'navizard app catalog and installed apps'
            || link === '/apps'
            || link.startsWith('/apps/');
    }

    private translateNavigation(menu: FuseNavigationItem[]): FuseNavigationItem[] {
        return menu.map((item) => this.translateItem(item));
    }

    private translateItem(item: FuseNavigationItem): FuseNavigationItem {
        const translated: FuseNavigationItem = { ...item };
        translated.title = this.translateKey(item.title as any);
        translated.subtitle = this.translateKey(item.subtitle as any);
        translated.tooltip = this.translateKey(item.tooltip as any);
        translated.hidden = this.normalizeHidden(item.hidden as any);
        if (translated.badge?.title) {
            translated.badge = {
                ...translated.badge,
                title: this.translateKey(translated.badge.title as any),
            };
        }
        if (item.children?.length) {
            translated.children = item.children.map((child) => this.translateItem(child));
        }
        return translated;
    }

    private translateKey(value?: string): string | undefined {
        if (!value || typeof value !== 'string') {
            return value as any;
        }
        const translated = this._translate.instant(value);
        return translated && translated !== value ? translated : value;
    }

    private normalizeHidden(value?: boolean | ((item: FuseNavigationItem) => boolean)): ((item: FuseNavigationItem) => boolean) | undefined {
        if (typeof value === 'function') {
            return value;
        }
        if (value) {
            return () => true;
        }
        return undefined;
    }
}
